import { useRef, useState } from 'react';

import { AlertCircleIcon, ChevronDownIcon, ChevronUpIcon, ExternalLinkIcon } from 'lucide-react';
import { Trans, useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import useBrains from '@/hooks/useBrains';
import useCollections from '@/hooks/useCollections';
import {
  closeDeleteCollectionModal,
  setIsDeleteCollectionModalOpen,
} from '@/redux/collections/actions';
import { selectIsDeleteCollectionModalOpen } from '@/redux/collections/selectors';
import { setCollectionId } from '@/redux/session/actions';
import { selectAccountSlug, selectCollectionId } from '@/redux/session/selectors';

export const DeleteCollectionModal = () => {
  // Redux state
  const slug = useSelector(selectAccountSlug);
  const isDeleteCollectionModalOpen = useSelector(selectIsDeleteCollectionModalOpen);
  const collectionId = useSelector(selectCollectionId);
  // Custom hooks
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { collection, deleteCollectionAsync, deleteStatus } = useCollections(collectionId);
  const { brainsByLanguage } = useBrains();
  const navigate = useNavigate();

  // Local state
  const [showAll, setShowAll] = useState(false);
  const isDeleting = deleteStatus === 'pending';

  // Refs
  const deleteButtonRef = useRef<HTMLButtonElement>(null);

  // Local variables
  const connectedAgents = brainsByLanguage(collection?.language)?.filter(
    (b) => b.collection_id === collectionId
  );
  const hasMoreAgents = connectedAgents.length > 3;
  const displayedAgents = showAll ? connectedAgents : connectedAgents.slice(0, 3);

  // Handlers
  const handleOpenChange = (open: boolean) => {
    dispatch(setIsDeleteCollectionModalOpen(open));
  };

  const closeModal = () => {
    dispatch(setCollectionId(null));
    dispatch(closeDeleteCollectionModal());
  };

  const handleDelete = () => {
    deleteCollectionAsync(
      { collection_id: collectionId, force: true },
      {
        onSuccess: () => {
          closeModal();
          navigate(`/${slug}/knowledge-bases`, { replace: true });
        },
      }
    );
  };

  const handleOpenAutoFocus = (e: Event) => {
    e.preventDefault();
    deleteButtonRef.current?.focus();
  };

  return (
    <Dialog open={isDeleteCollectionModalOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="gap-6 sm:max-w-md" onOpenAutoFocus={handleOpenAutoFocus}>
        <DialogHeader>
          <DialogTitle className="w-96 break-all">
            {t('common.delete')} {collection?.name}
          </DialogTitle>
          <DialogDescription className="w-96 break-all">
            <Trans i18nKey="collections.delete_warning" values={[collection?.name]} />
          </DialogDescription>
        </DialogHeader>

        {connectedAgents.length > 0 && (
          <div className="space-y-4">
            <Alert variant="destructive" className="border-destructive/50 text-destructive">
              <AlertCircleIcon size={16} />
              <AlertTitle>{t('collections.modals.delete_collection_alert_title')}</AlertTitle>
              <AlertDescription>
                {t('collections.modals.delete_collection_alert_subtitle')}
              </AlertDescription>
            </Alert>

            {displayedAgents.length > 0 && (
              <div className="space-y-2 py-4">
                <h3 className="text-sm leading-6 font-medium text-muted-foreground">
                  {t('collections.connected_ai_agents')}
                </h3>

                <div className="flex flex-wrap gap-x-4 gap-y-2">
                  {displayedAgents.map((ai_agent) => (
                    <Button
                      key={ai_agent.brain_id}
                      onClick={() => {
                        handleOpenChange(false);
                        navigate(`/${slug}/ai-agents/${ai_agent.brain_id}`);
                      }}
                      variant="link"
                      className="h-6 min-w-auto p-0"
                    >
                      {ai_agent.name}
                      <ExternalLinkIcon size={16} />
                    </Button>
                  ))}
                </div>

                {hasMoreAgents && (
                  <Button
                    variant="ghost-neutral"
                    className="h-auto p-0 text-sm leading-6 font-medium text-muted-foreground hover:bg-transparent hover:text-foreground"
                    onClick={() => setShowAll(!showAll)}
                  >
                    {showAll ? (
                      <>
                        {t('common.show_less')}
                        <ChevronUpIcon size={16} />
                      </>
                    ) : (
                      <>
                        {t('common.show_more')}
                        <ChevronDownIcon size={16} />
                      </>
                    )}
                  </Button>
                )}
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end gap-2">
          <Button variant="ghost-neutral" onClick={closeModal}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="destructive"
            className="text-destructive focus:ring-2 focus:ring-destructive-soft focus:ring-offset-2"
            onClick={handleDelete}
            ref={deleteButtonRef}
            isLoading={isDeleting}
          >
            {t('common.delete')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DeleteCollectionModal;
